<template>
    <page :busy="busy" :tabs="tabs" :compact-mobile-view="true">
        <grid
            ref="grid"
            :defaultColDef="defaultColDef"
            :columnDefs="columnDefs"
            :treeData="false"
            :getDataPath="getDataPath"
            :isExternalFilterPresent="isExternalFilterPresent"
            :doesExternalFilterPass="doesExternalFilterPass"
            :rowData="tooltips"
        >
            <responsive-button
                :icon="['fas', 'plus']"
                modal="create-tooltip-modal"
                variant="primary"
                >
                Create a tooltip
            </responsive-button>
            <CreateTooltipModal
                @startCreating="modalCreateClicked"
                @endCreating="modalEndCreate"
                :options="options"
            />
        </grid>
    </page>
</template>


<script>
import { mapGetters } from 'vuex';
import API from '../../../../api'
import Page from '../../../generic/page/page.vue'
import Grid from '../../../generic/grid/grid.vue';
import ActionsCellRenderer from './actions-cell-renderer.vue';
import ResponsiveButton from '../../../generic/responsive-button/responsive-button.vue';
import CreateTooltipModal from './create-tooltip-modal.vue';

export default {
    name: 'PartnerAdminTooltips',
    components: {
        Page, Grid, ResponsiveButton, CreateTooltipModal, actionsCellRenderer: ActionsCellRenderer,
    },
    data() {
        return {
            tabs: [
                {
                    title: 'Partner card categories',
                    route: '/app/partner-cards/admin/',
                    active: false,
                },
                {
                    title: 'Partner card data',
                    route: '/app/partner-cards/admin/card-data/',
                    active: false,
                },
                {
                    title: 'Partner card tooltips',
                    route: '/app/partner-cards/admin/card-tooltips/',
                    active: true,
                },
            ],
            tooltips: [],
            busy: false,
            defaultColDef: {
                autoHeight: true,
                sortable: false,
            },
            getDataPath: data => data.id,
            options: [{value: null, text:'Select partner'}]
        };
    },

    computed: {
        ...mapGetters('partner', ['partnerIds', 'partnerTitleById']),
        columnDefs() {
            return [
                {
                    field: 'id',
                    headerName: 'ID',
                    editable: false,
                    maxWidth: 90,
                },
                {
                    type: 'editableColumn',
                    field: 'text',
                    headerName: 'Span text',
                    sort: 'asc',
                    flex: 1,
                    minWidth: 150,
                    editable: true,
                    valueSetter: ({ oldValue, newValue, node, data }) => {
                        (async () => {
                            try {
                                await API.put(
                                    `/app/partner-cards/tooltip/${data.id}/`,
                                    {
                                        text: newValue,
                                    }
                                );
                                const message = '#' + data.id + ' Span text has been updated to ' + newValue;
                                this.successToast({text: message})
                            } catch (error) {
                                this.errorToast({text: 'Could not save new text.'})
                                data.text = oldValue;
                                node.setData(data);
                            }
                        })();
                        data.text = newValue;
                        return true;
                    },
                },
                {
                    type: 'editableColumn',
                    field: 'hover_text',
                    headerName: 'Text on hover',
                    sort: 'asc',
                    flex: 1,
                    minWidth: 110,
                    editable: true,
                    valueSetter: ({ oldValue, newValue, node, data }) => {
                        (async () => {
                            try {
                                await API.put(
                                    `/app/partner-cards/tooltip/${data.id}/`,
                                    {
                                        hover_text: newValue,
                                    }
                                );
                                const message = '#' + data.id + ' Text on hover has been updated to ' + newValue;
                                this.successToast({text: message})
                            } catch (error) {
                                this.errorToast({text: 'Could not save text.'})
                                data.hover_text = oldValue;
                                node.setData(data);
                            }
                        })();
                        data.hover_text = newValue;
                        return true;
                    },
                },
                {
                    field: 'partner',
                    headerName: 'Partner',
                    flex: 1,
                    minWidth: 120,
                    cellRenderer: ({ data, value }) => this.partnerTitleById(value),
                    editable: true,
                    cellEditor: 'agRichSelectCellEditor',
                    cellEditorParams: () => ({
                        values: this.partnerIds,
                        cellRenderer: ({ value }) =>
                            this.partnerTitleById(value),
                    }),
                    valueSetter: ({ oldValue, newValue, node, data }) => {
                        (async () => {
                            try {
                                await API.put(
                                    `/app/partner-cards/tooltip/${data.id}/`,
                                    {
                                        partner: newValue,
                                    }
                                );
                                this.successToast({text: 'Now tooltip is available on partner card #' + newValue})
                            } catch (error) {
                                this.errorToast({text:  'Could not save tooltip.'})
                                data.partner = oldValue;
                                node.setData(data);
                            }
                        })();
                        data.partner = newValue;

                        return true;

                    },
                },
                {
                    headerName: '',
                    maxWidth: 100,
                    resizable: false,
                    cellStyle: {textAlign: "right"},
                    cellRenderer: 'actionsCellRenderer',
                },
            ]
        },
        isExternalFilterPresent() {
            return () => false;
        },
        doesExternalFilterPass() {
            return node => !node.data.deleted;
        },
    },

    mounted() {
        (async () => {
            this.busy = true;
            try {
                await this.$store.dispatch('partner/fetchPartners');
                for (let i = 0; i < this.partnerIds.length; i++) {
                this.options.push({
                    value: this.partnerIds[i],
                    text: this.partnerTitleById(this.partnerIds[i])
                })
            }
            } catch (err) {
                this.errorToast({text: 'Could not load partner data.'})
            }
            await this.fetchTooltips();
            this.busy = false;
        })();

    },
    beforeCreate() {
        document.body.className = 'lx-partner-card-tooltips-admin';
    },

    methods: {
        async fetchTooltips() {
            try {
                const response = await API.get('/app/partner-cards/tooltip/');
                this.tooltips = response.data;
            } catch (err) {
                this.errorToast({text: 'Could not load tooltips data.'})
            }
        },
        modalCreateClicked() {
            this.busy = true;
        },
        async modalEndCreate() {
           await this.fetchTooltips();
           this.busy = false;
        },
    }
}
</script>

<style lang="scss">
    body.lx-partner-card-tooltips-admin {
        overflow: hidden;
    }
</style>
